import React from "react";
import { CalculatorInput } from "@/components/ui/calculator-input";
import { Button } from "@/components/ui/button";
import { Copy, AlertTriangle, HelpCircle, ArrowLeft, ArrowRight } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate } from "react-router-dom";
import { CALIBRATION_KEYS, CalibrationMetadata } from "@/constants/calibrationKeys";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

interface FastModeProps {
  selectedMethod: "classic" | "yolo";
  onMethodChange: (method: "classic" | "yolo") => void;
  initialFlowRatio: string;
  onInitialFlowRatioChange: (value: string) => void;
  modifier: string;
  onModifierChange: (value: string) => void;
  newFlowRatio: string;
  onGoToTutorial: () => void;
}

const FastMode = ({
  selectedMethod,
  onMethodChange,
  initialFlowRatio,
  onInitialFlowRatioChange,
  modifier,
  onModifierChange,
  newFlowRatio,
  onGoToTutorial
}: FastModeProps) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [currentPass, setCurrentPass] = React.useState<1 | 2>(1);
  const [rememberMode, setRememberMode] = React.useState(false);
  const [pass2Completed, setPass2Completed] = React.useState(false);

  // Load saved preference on component mount
  React.useEffect(() => {
    const savedMode = localStorage.getItem('flow-calculator-mode');
    if (savedMode === 'fast') {
      setRememberMode(true);
    }
  }, []);
  
  const handleRememberModeChange = (checked: boolean) => {
    setRememberMode(checked);
    if (checked) {
      localStorage.setItem('flow-calculator-mode', 'fast');
    } else {
      localStorage.removeItem('flow-calculator-mode');
    }
  };

  // Normalize decimal separators to standard decimal point
  const normalizeDecimalSeparator = (value: string) => {
    // Replace comma, apostrophe, and Arabic decimal separator with decimal point
    return value.replace(/[,''٫]/g, '.');
  };

  // Detect the decimal separator used in the input and format the result accordingly
  const formatWithOriginalSeparator = (value: string, originalInput: string) => {
    if (!value) return value;

    // Find which separator was used in the original input
    let usedSeparator = '.';
    if (originalInput.includes(',')) usedSeparator = ',';else if (originalInput.includes("'")) usedSeparator = "'";else if (originalInput.includes('٫')) usedSeparator = '٫';

    // If the original used a different separator, replace the decimal point
    if (usedSeparator !== '.') {
      return value.replace('.', usedSeparator);
    }
    return value;
  };
  const getSliderOptions = () => {
    if (selectedMethod === "classic") {
      return currentPass === 1 ? [-20, -15, -10, -5, 0, 5, 10, 15, 20] : [-9, -8, -7, -6, -5, -4, -3, -2, -1, 0];
    } else {
      return currentPass === 1 ? [-0.05, -0.04, -0.03, -0.02, -0.01, 0, 0.01, 0.02, 0.03, 0.04, 0.05] : [-0.04, -0.035, -0.03, -0.025, -0.02, -0.015, -0.01, -0.005, 0, 0.005, 0.01, 0.015, 0.02, 0.025, 0.03, 0.035];
    }
  };
  const sliderOptions = getSliderOptions();
  const zeroIndex = sliderOptions.indexOf(0);
  const getCurrentSliderValue = () => {
    if (!modifier) return [zeroIndex];
    const modifierValue = parseFloat(modifier);
    const index = sliderOptions.findIndex(val => val === modifierValue);
    return [index >= 0 ? index : zeroIndex];
  };
  const handleSliderChange = (value: number[]) => {
    const selectedValue = sliderOptions[value[0]];
    onModifierChange(selectedValue.toString());
  };
  const getFlowRatioStatus = () => {
    if (!newFlowRatio) return "neutral";
    const normalizedValue = normalizeDecimalSeparator(newFlowRatio);
    const value = parseFloat(normalizedValue);
    if (value > 1.2 || value < 0.8) return "warning";
    return "good";
  };
  const isInitialFlowRatioWarning = () => {
    if (!initialFlowRatio) return false;
    const normalizedValue = normalizeDecimalSeparator(initialFlowRatio);
    const value = parseFloat(normalizedValue);
    return value > 1.2 || value < 0.8;
  };
  const copyAndUpdate = () => {
    if (newFlowRatio) {
      const formattedValue = formatFlowRatio(newFlowRatio);
      navigator.clipboard.writeText(formattedValue);
      onInitialFlowRatioChange(formattedValue);

      // Mark Pass 2 as completed for UI state only
      if (currentPass === 2) {
        setPass2Completed(true);
      }
      setCurrentPass(2);
      onModifierChange("");
    }
  };
  
  const handleGoToTutorial = () => {
    // Reset all states before switching to tutorial mode
    onInitialFlowRatioChange("");
    onModifierChange("");
    setCurrentPass(1);
    setPass2Completed(false);
    onGoToTutorial();
  };
  
  const handleBackToTools = () => {
    navigate('/tools');
  };

  const handleFinish = () => {
    // Mark calibration as completed when Done button is clicked
    const calibrationData: CalibrationMetadata = {
      completed: true,
      completedAt: new Date().toISOString(),
      slicerUsed: "orca" // Default to orca for fast mode
    };
    localStorage.setItem(CALIBRATION_KEYS.flow, JSON.stringify(calibrationData));
    
    navigate('/tools');
  };

  // Format the flow ratio to remove trailing zeros and preserve original separator
  const formatFlowRatio = (value: string) => {
    const normalizedValue = normalizeDecimalSeparator(value);
    const num = parseFloat(normalizedValue);
    if (isNaN(num)) return value;
    const formattedNum = num.toString();
    return formatWithOriginalSeparator(formattedNum, initialFlowRatio);
  };
  const getInputBorderClass = () => {
    if (!initialFlowRatio) return "border-gray-400 dark:border-gray-600";
    const normalizedValue = normalizeDecimalSeparator(initialFlowRatio);
    const value = parseFloat(normalizedValue);
    if (value > 1.2 || value < 0.8) return "border-red-500";
    return "border-gray-400 dark:border-gray-600";
  };
  const getInputValueClass = () => {
    if (!initialFlowRatio) return "";
    const normalizedValue = normalizeDecimalSeparator(initialFlowRatio);
    const value = parseFloat(normalizedValue);
    if (value > 1.2 || value < 0.8) return "text-red-600 dark:text-red-400";
    return "";
  };
  const getCenterLabel = () => {
    // For classic pass 2, the center value is not 0, so we need to find the actual center value
    if (selectedMethod === "classic" && currentPass === 2) {
      const centerIndex = Math.floor(sliderOptions.length / 2);
      return sliderOptions[centerIndex].toString();
    }
    return "0";
  };
  const validateInput = (inputValue: string) => {
    // Allow empty string
    if (inputValue === '') return true;

    // Check against pattern: digits, optional decimal separator (., comma, apostrophe, Arabic), more digits
    const pattern = /^[0-9]*[.,''٫]?[0-9]*$/;
    return pattern.test(inputValue);
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // Validate the input
    if (validateInput(newValue)) {
      onInitialFlowRatioChange(newValue);
    }
  };
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        {/* Combined Toggle Control */}
        <div className="p-1">
          <div className={`grid ${isMobile ? 'grid-cols-1 gap-1' : 'grid-cols-2 gap-1'}`}>
            {/* Method Toggle */}
            <div className="rounded-md p-1">
              <div className="flex h-9">
                <button type="button" onClick={() => onMethodChange("classic")} className={`flex-1 rounded-sm px-3 py-1.5 text-sm font-medium transition-all ${selectedMethod === "classic" ? "bg-green-50 dark:bg-green-950 text-neutral-900 dark:text-white shadow-sm border border-green-200 dark:border-green-600 font-bold" : "text-gray-400 dark:text-gray-500 border border-gray-300 dark:border-gray-600"}`}>
                  Classic
                </button>
                <button type="button" onClick={() => onMethodChange("yolo")} className={`flex-1 rounded-sm px-3 py-1.5 text-sm font-medium transition-all ${selectedMethod === "yolo" ? "bg-green-50 dark:bg-green-950 text-neutral-900 dark:text-white shadow-sm border border-green-200 dark:border-green-600 font-bold" : "text-gray-400 dark:text-gray-500 border border-gray-300 dark:border-gray-600"}`}>
                  YOLO
                </button>
              </div>
            </div>
            
            {/* Pass Toggle */}
            <div className="rounded-md p-1">
              <div className="flex h-9">
                <button type="button" onClick={() => {
                setCurrentPass(1);
                onModifierChange("");
              }} className={`flex-1 rounded-sm px-3 py-1.5 text-sm font-medium transition-all ${currentPass === 1 ? "bg-green-50 dark:bg-green-950 text-neutral-900 dark:text-white shadow-sm border border-green-200 dark:border-green-600 font-bold" : "text-gray-400 dark:text-gray-500 border border-gray-300 dark:border-gray-600"}`}>
                  Pass 1
                </button>
                <button type="button" onClick={() => {
                setCurrentPass(2);
                onModifierChange("");
              }} className={`flex-1 rounded-sm px-3 py-1.5 text-sm font-medium transition-all ${currentPass === 2 ? "bg-green-50 dark:bg-green-950 text-neutral-900 dark:text-white shadow-sm border border-green-200 dark:border-green-600 font-bold" : "text-gray-400 dark:text-gray-500 border border-gray-300 dark:border-gray-600"}`}>
                  Pass 2
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <label className="text-base font-semibold">
            {currentPass === 2 ? "Flow Ratio" : "Initial Flow Ratio"}
          </label>
          <input 
            id="initial-flow-quick" 
            type="text" 
            inputMode="decimal" 
            placeholder="" 
            value={initialFlowRatio} 
            onChange={handleInputChange} 
            className={cn(
              "w-full text-lg px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500",
              "flex h-10 w-full rounded-md border border-input bg-surface backdrop-blur-sm px-3 py-2 text-sm text-foreground ring-offset-background",
              "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200",
              isInitialFlowRatioWarning() ? "border-red-500 text-red-600 dark:text-red-400" : ""
            )}
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-base font-semibold">
              Modifier{modifier && `: ${modifier}`}
            </label>
          </div>
          <div className="space-y-4">
            <Slider value={getCurrentSliderValue()} onValueChange={handleSliderChange} max={sliderOptions.length - 1} min={0} step={1} className="w-full" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{sliderOptions[0]}</span>
              <span className="font-medium">{getCenterLabel()}</span>
              <span>{sliderOptions[sliderOptions.length - 1]}</span>
            </div>
          </div>
        </div>
      </div>
      
      {newFlowRatio && (
        <>
          <div className={`p-4 rounded-lg border-2 ${getFlowRatioStatus() === "warning" ? "bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800" : "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-600"}`}>
            <div className="space-y-3">
              <div className="text-center">
                <div className="text-base font-semibold mb-2">New Flow Ratio</div>
                <div className={`text-3xl font-bold ${getFlowRatioStatus() === "warning" ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}`}>
                  {formatFlowRatio(newFlowRatio)}
                </div>
              </div>
              
              <div className="flex justify-center">
                <Button onClick={copyAndUpdate} variant="outline" size="sm" className="border-gray-300 dark:border-gray-600">
                  <Copy className="h-4 w-4 mr-2" />
                  Copy and update
                </Button>
              </div>
            </div>
          </div>
          
          {getFlowRatioStatus() === "warning" && <div className="p-4 rounded-lg border-2 bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800">
              <div className="flex items-center justify-center gap-2 text-sm text-red-600 dark:text-red-400">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span className="text-center">Warning: This value is outside the typical recommended range.</span>
              </div>
            </div>}
        </>
      )}
      
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2">
          <Button 
            onClick={handleBackToTools} 
            variant="outline" 
            className="min-h-[44px] border-gray-300 dark:border-gray-600"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          {pass2Completed ? (
            <Button 
              onClick={handleFinish}
              variant="outline" 
              className="min-h-[44px] border-gray-300 dark:border-gray-600"
            >
              Done
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={handleGoToTutorial} 
              variant="outline" 
              className="min-h-[44px] border-gray-300 dark:border-gray-600"
            >
              <HelpCircle className="h-4 w-4 mr-2" />
              Tutorial Mode
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FastMode;
